import type { AdminUserListItem, AdminUserListResponse } from "api/types/user.types";

/**
 * 관리자 회원 목록 응답 어댑터 — **BE 롤아웃 창 한정 임시 호환 계층**.
 *
 * ⚠️ TODO(제거): BE PR 507 (`Bucketlist-Class/BE#507`) 배포가 안정화되면 이 파일과
 * 호출부(`usersService.getList`)의 어댑터 적용, 그리고 관련 테스트
 * (`admin-user-list.maint.20260803-rollout-compat.test.ts`,
 *  `UserList.maint.20260803-rollout-compat.test.tsx`)를 함께 삭제한다.
 * 남겨두면 "배열도 정상 계약" 이라는 오해를 만들고, 이 PR 이 닫은 무제한 전량 조회
 * 경로가 슬그머니 되살아난다.
 *
 * ## 왜 필요한가
 * 배포는 원자적이지 않다. FE 번들이 먼저 전파되는 창(그리고 배포 전부터 열려 있던 탭)이
 * 반드시 존재하고, 그동안 목록 엔드포인트는 **구 응답(bare array)** 을 돌려준다.
 * 신 FE 는 `data.results` 를 읽으므로 `undefined.length` 로 렌더가 통째로 죽는다
 * (실측: `document.body.textContent.length === 0` — 설명도 복구 경로도 없는 백지).
 *
 * ## 계약
 * 1. envelope 은 그대로 통과시킨다.
 * 2. bare array 는 envelope 형태로 감싸되 **원문 PII 를 전부 버린다**.
 *    구 응답에는 `hp`·`user_email`·`addresses`·`profile_image_url`·`bio`·`tags` 원문이
 *    들어 있는데, 목록에서 그것들을 없애는 것이 이 PR 의 목적이다. 롤아웃 호환을
 *    이유로 원문을 되살리면 PR 의 의도를 정면으로 뒤집는다.
 * 3. 마스킹 필드는 구 응답에 없으므로 `''`(연락처 없음)으로 sanitize 한다.
 *    원문에서 즉석 마스킹을 만들지 않는다 — 그 자체가 원문을 다루는 일이다.
 * 4. **배열을 새 정상 계약으로 승격하지 않는다.** `count` 는 받은 길이일 뿐이고
 *    `next`/`previous` 는 항상 `null` 이라 페이지네이션이 확장되지 않는다.
 *    구 BE 는 전량을 한 번에 주므로 화면은 "받은 것 그대로 한 페이지" 로만 동작한다.
 */

type Unknown = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);
const num = (v: unknown): number | null => (typeof v === "number" ? v : null);

/**
 * 구 응답 1행 → 목록 항목. **화이트리스트 방식**이라 알려진 안전 필드만 통과한다.
 * 새 원문 필드가 구 응답에 추가돼도 자동으로 새어나가지 않는다.
 */
const fromRow = (raw: Unknown): AdminUserListItem => ({
  id: typeof raw.id === "number" ? raw.id : 0,
  public_id: str(raw.public_id),
  user_id: str(raw.user_id),
  name: str(raw.name),
  nickname: str(raw.nickname),
  // 마스킹 필드가 오면 쓰고, 없으면 "없음" 으로 둔다 — 원문에서 만들지 않는다.
  hp_masked: str(raw.hp_masked),
  user_email_masked: str(raw.user_email_masked),
  // 목록 queryset 은 활성 회원만 담는 계약이라 결측 시 true 가 안전한 기본값이다.
  is_active: bool(raw.is_active, true),
  is_admin: bool(raw.is_admin, false),
  is_staff: bool(raw.is_staff, false),
  home_brand_id: num(raw.home_brand_id),
  home_brand_name: typeof raw.home_brand_name === "string" ? raw.home_brand_name : null,
  // BE 가 null 을 줄 수 있다 — 빈 문자열로 뭉개지 않고 그대로 흘린다.
  date_joined: typeof raw.date_joined === "string" ? raw.date_joined : null,
});

/**
 * DRF pagination envelope 계약을 **엄격히** 검사한다.
 *
 * `"count" in data` 만 보면 `{count: "3", results: "x"}` 같은 깨진 응답이 통과해
 * `count 0 · 결과 0건` 으로 렌더된다 — 관리자는 그것을 "회원이 없다" 로 읽는다.
 * 계약을 벗어나면 성공이 아니라 **오류**로 올려 보낸다.
 */
const isPageCount = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v) && v >= 0;
const isPageLink = (v: unknown): boolean => v === null || v === undefined || typeof v === "string";

const isEnvelope = (data: unknown): data is AdminUserListResponse => {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return false;
  const d = data as Unknown;
  if (!("count" in d)) return false;
  // `Number.isInteger` 가 NaN·Infinity·소수를 함께 걸러낸다.
  return isPageCount(d.count) && Array.isArray(d.results) && isPageLink(d.next) && isPageLink(d.previous);
};

const toRows = (rows: unknown): AdminUserListItem[] => (Array.isArray(rows) ? rows.filter((row): row is Unknown => typeof row === "object" && row !== null).map(fromRow) : []);

/**
 * 알 수 없는 응답 형태. **성공 0건과 구분해야 한다** — 조용히 빈 목록을 돌려주면
 * 관리자가 "회원이 없다" 로 읽는다.
 */
export class UnknownUserListResponseError extends Error {
  constructor() {
    super("회원 목록 응답 형식을 알 수 없습니다.");
    this.name = "UnknownUserListResponseError";
  }
}

export const toAdminUserListResponse = (data: unknown): AdminUserListResponse => {
  if (Array.isArray(data)) {
    const results = toRows(data);
    // 호출부가 형태를 알아야 페이지 상태를 정규화할 수 있다 — 배열은 항상 한 페이지다.
    return { count: results.length, next: null, previous: null, results, isLegacyArray: true };
  }

  if (isEnvelope(data)) {
    // **envelope 도 whitelist 를 거친다.** 혼합 배포에서 신 엔드포인트가 구 serializer 로
    // 응답하면 `hp`·`user_email`·`addresses` 원문이 그대로 화면 상태로 들어온다.
    // 이 어댑터의 목적은 "배열 방어" 가 아니라 **원문 PII 차단**이다.
    return {
      count: data.count,
      next: data.next ?? null,
      previous: data.previous ?? null,
      results: toRows(data.results),
      isLegacyArray: false,
    };
  }

  // envelope 를 자칭하지만 계약을 어긴 응답도 여기로 떨어진다 — 조용한 0건으로 뭉개지 않는다.
  throw new UnknownUserListResponseError();
};

/**
 * **회원 상세 라우팅** 전용 식별자 — `public_id` 만 유효하다.
 *
 * `/user-list/{id}` 는 `public_id` 로 여는 경로다. `user_id` 를 여기에 넣으면 존재하지
 * 않는 경로로 보내는 셈이라, 결측이면 **빈 문자열**을 돌려주고 호출부가 이동하지 않는다.
 *
 * 결측은 정상 경로가 아니다 — BE `origin/dev`(`4d52322`) 의 `AdminUserSerializer` 는
 * `UserSelfSerializer` 를 상속하고 그 `Meta.fields` 첫 항목이 `public_id` 라, 구 배열
 * 응답에도 `public_id` 가 실린다. 이 가드는 그 계약이 깨졌을 때의 안전망이다.
 */
export const memberDetailRouteId = (row: Pick<AdminUserListItem, "public_id">): string => row.public_id || "";

/**
 * **회원 대상 mutation** 전용 식별자 — `public_id` 우선, 없으면 `user_id`.
 *
 * 라우팅과 달리 서버는 BE `resolve_user_by_identifier` 로 `public_id` 와 `user_id` 를
 * **모두** 받는다. 그래서 강제 예약 같은 쓰기 요청은 둘 중 실재하는 값으로 보낼 수 있다.
 * 둘 다 없으면 빈 문자열이고, 호출부가 요청을 만들지 않는다.
 *
 * 상세 라우팅에는 쓰지 않는다 — 그 용도는 `memberDetailRouteId` 다.
 */
export const memberMutationId = (row: Pick<AdminUserListItem, "public_id" | "user_id">): string => row.public_id || row.user_id || "";
